import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { CloudUpload, TaskAlt } from '@mui/icons-material';
import { API_BASE } from '../api';

const formatDateTime = (value) => {
  if (!value) return '-';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return value;
  }
};

export default function CadastroEmpreiteiraDocumentoExterno() {
  const { accessToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadContext = async () => {
      if (!accessToken) {
        setError('Link inválido.');
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(
          `${API_BASE}/cadastro-empreiteiras/documentos/edicao/${encodeURIComponent(accessToken)}`
        );
        const text = await res.text();
        const data = text ? JSON.parse(text) : null;

        if (!res.ok) {
          throw new Error(data?.detail || data?.error || 'Link inválido ou expirado.');
        }

        setContext(data);
      } catch (err) {
        setError(err.message || 'Não foi possível validar o link de atualização.');
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [accessToken]);

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !accessToken) return;

    setError('');
    setSuccess('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await fetch(
        `${API_BASE}/cadastro-empreiteiras/documentos/edicao/${encodeURIComponent(accessToken)}`,
        {
          method: 'POST',
          body: formData,
        }
      );

      const text = await res.text();
      const data = text ? JSON.parse(text) : null;

      if (!res.ok) {
        throw new Error(data?.detail || data?.error || 'Falha ao atualizar o documento.');
      }

      setSuccess(data?.message || 'Documento atualizado com sucesso. O link foi expirado.');
      setContext((prev) =>
        prev
          ? {
              ...prev,
              documento_nome: file.name,
            }
          : prev
      );
    } catch (err) {
      setError(err.message || 'Não foi possível concluir a atualização do documento.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #f8fbff 0%, #eef3fb 45%, #f5f6fa 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Atualização de documento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use este link exclusivo para substituir o documento solicitado.
            </Typography>

            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Validando link...</Typography>
              </Stack>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}
            {success ? (
              <Alert severity="success" icon={<TaskAlt fontSize="inherit" />}>
                {success}
              </Alert>
            ) : null}

            {!loading && !error && context ? (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack spacing={0.8}>
                  <Typography variant="body2">
                    <strong>Protocolo:</strong> {context.protocolo || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Empresa:</strong> {context.empresa || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Documento:</strong> {context.documento_label || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Arquivo atual:</strong> {context.documento_nome || '-'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Expira em:</strong> {formatDateTime(context.expires_at)}
                  </Typography>
                </Stack>
              </Paper>
            ) : null}

            {!loading && !error && !success ? (
              <Button
                component="label"
                variant="contained"
                startIcon={<CloudUpload />}
                disabled={uploading}
              >
                {uploading ? 'Enviando...' : 'Enviar novo documento'}
                <input hidden type="file" onChange={handleUpload} />
              </Button>
            ) : null}

            <Typography variant="caption" color="text.secondary">
              Após o envio, este link será automaticamente expirado por segurança.
            </Typography>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
